import { Link } from 'react-router-dom';
import { AdmissionReadinessCard } from './AdmissionReadinessCard';
import { AdmissionStatus } from './AdmissionStatus';
import {
  formatLegalRegimes,
  instrumentTypeLabel,
  sourceFormLabel,
  vaultRoleLabel,
} from '../../admission/labels';
import { admissionNextActionLabel } from '../../admission/selectors';

export function PackageAdmissionOverview({
  admission,
  onOpenDocument,
  exceptions = [],
}) {
  if (!admission) return null;
  const nextAction = admissionNextActionLabel(admission);
  const documents = admission.document_assessments || [];
  const admissionExceptions = (exceptions.length ? exceptions : admission.exceptions || []).filter(
    (item) => String(item.code || '').startsWith('ADM-')
  );

  return (
    <section className="space-y-4" aria-labelledby="admission-overview-heading">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <h2 id="admission-overview-heading" className="text-sm font-semibold text-eorig-ink">
          Admission overview
        </h2>
        {nextAction ? (
          <p className="text-[12px] font-semibold text-eorig-navy-600">{nextAction}</p>
        ) : null}
      </div>

      <AdmissionReadinessCard admission={admission} />

      <div className="rounded-2xl border border-eorig-border bg-white p-4 shadow-eorig-card">
        <dl className="grid gap-2 text-[13px] sm:grid-cols-2">
          <div>
            <dt className="text-[11px] font-semibold uppercase text-eorig-muted">
              Programme profile version
            </dt>
            <dd>{admission.admission_profile_version ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold uppercase text-eorig-muted">Primary document</dt>
            <dd className="font-mono text-[12px]">{admission.primary_document_id || '—'}</dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold uppercase text-eorig-muted">eVault treatment</dt>
            <dd>{admission.intended_treatment || '—'}</dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold uppercase text-eorig-muted">Policy hash</dt>
            <dd className="break-all font-mono text-[11px]">{admission.admission_policy_hash || '—'}</dd>
          </div>
        </dl>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-eorig-border bg-white shadow-eorig-card">
        <table className="min-w-full text-left text-[13px]">
          <caption className="sr-only">Document admission assessments</caption>
          <thead className="border-b border-eorig-border bg-eorig-surface text-[11px] uppercase tracking-wide text-eorig-muted">
            <tr>
              <th className="px-3 py-2 font-semibold">Document</th>
              <th className="px-3 py-2 font-semibold">Document role</th>
              <th className="px-3 py-2 font-semibold">Source form</th>
              <th className="px-3 py-2 font-semibold">Admission check</th>
              <th className="px-3 py-2 font-semibold">Legal classification</th>
              <th className="px-3 py-2 font-semibold">eVault treatment</th>
              <th className="px-3 py-2 font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {documents.length ? (
              documents.map((row) => (
                <tr key={row.document_id} className="border-b border-eorig-border last:border-b-0">
                  <td className="px-3 py-2 font-mono text-[12px]">{row.document_id}</td>
                  <td className="px-3 py-2">{vaultRoleLabel(row.vault_role)}</td>
                  <td className="px-3 py-2">{sourceFormLabel(row.source_form)}</td>
                  <td className="px-3 py-2">
                    {row.admission_check ? (
                      <AdmissionStatus kind="check" status={row.admission_check} />
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {instrumentTypeLabel(row.instrument_type)}
                    <span className="mt-0.5 block text-[11px] text-eorig-muted">
                      {formatLegalRegimes(row.legal_regimes)}
                    </span>
                  </td>
                  <td className="px-3 py-2">{vaultRoleLabel(row.vault_role)}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => onOpenDocument?.(row.document_id)}
                      className="text-[12px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
                    >
                      Review admission details
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td className="px-3 py-4 text-eorig-muted" colSpan={7}>
                  No document assessments yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {admissionExceptions.length ? (
        <div className="rounded-2xl border border-eorig-border bg-white p-4 shadow-eorig-card">
          <h3 className="text-sm font-semibold text-eorig-ink">Admission exceptions</h3>
          <ul className="mt-2 space-y-2">
            {admissionExceptions.map((item) => (
              <li key={item.id || item.code} className="text-[13px] text-eorig-ink">
                <Link
                  to={`/exceptions/${item.id}`}
                  state={{ exception: item }}
                  className="font-mono font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
                >
                  {item.code}
                </Link>
                {item.description ? ` — ${item.description}` : ''}
                {item.fact_code ? (
                  <span className="ml-1 text-eorig-muted">Fact {item.fact_code}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
